import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, ToastService } from '../../../../../../../libs/core';
import { of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { CrmAuthApiService } from './crm-auth-api.service';
import { CrmAuthCookieService } from './crm-auth-cookie.service';
import { CrmAuthFlowService } from './crm-auth-flow.service';
import { CrmOnboardingAccessService } from './crm-onboarding-access.service';
import { CrmPermissionStateService } from './crm-permission-state.service';
import { CrmBrandingStateService } from '../../../core/crm-branding-state.service';
import { isKnownAppUrl } from '../../../core/app-route-paths';

@Injectable({ providedIn: 'root' })
export class CrmAuthSessionService {
  private readonly lastProtectedRouteKey = 'crm.last-protected-route';
  private readonly defaultProtectedRoute = '/dashboard';
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly crmAuthApiService = inject(CrmAuthApiService);
  private readonly crmAuthCookieService = inject(CrmAuthCookieService);
  private readonly crmAuthFlowService = inject(CrmAuthFlowService);
  private readonly crmOnboardingAccessService = inject(CrmOnboardingAccessService);
  private readonly crmPermissionStateService = inject(CrmPermissionStateService);
  private readonly crmBrandingStateService = inject(CrmBrandingStateService);

  private logoutRequestInFlight = false;
  private sessionGeneration = 0;

  getSessionGeneration(): number {
    return this.sessionGeneration;
  }

  isAuthenticated(cleanupOnMismatch = false): boolean {
    const hasCookies = this.crmAuthCookieService.hasAuthCookies();
    let hasAppSession = this.authService.isAuthenticated();

    // Refresh-safe rehydrate: cookies may exist before in-memory/core session is available.
    if (hasCookies && !hasAppSession) {
      this.authService.hydrate();
      hasAppSession = this.authService.isAuthenticated();
    }

    if (hasCookies && hasAppSession) {
      return true;
    }

    if (cleanupOnMismatch) {
      this.clearSessionArtifacts();
    }

    return false;
  }

  setLastProtectedRoute(url: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (!this.isProtectedRoute(url)) {
      return;
    }

    localStorage.setItem(this.lastProtectedRouteKey, url);
  }

  getLastProtectedRoute(): string {
    if (typeof window === 'undefined') {
      return this.defaultProtectedRoute;
    }

    const saved = localStorage.getItem(this.lastProtectedRouteKey);
    if (!saved || !this.isProtectedRoute(saved)) {
      return this.defaultProtectedRoute;
    }

    return saved;
  }

  clearLastProtectedRoute(): void {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.removeItem(this.lastProtectedRouteKey);
  }

  logout(toastMessage?: string): void {
    if (this.logoutRequestInFlight) {
      return;
    }

    this.logoutRequestInFlight = true;
    this.sessionGeneration++;

    const refreshToken = this.crmAuthCookieService.getRefreshToken();

    if (!refreshToken) {
      this.clearSessionArtifacts();
      if (toastMessage) {
        this.toastService.show(toastMessage, 'error');
      }
      void this.router.navigateByUrl('/login', { replaceUrl: true });
      // logoutRequestInFlight stays true — reset by resetLogoutLock() after a new login.
      return;
    }

    this.crmAuthApiService
      .logout({ refreshToken })
      .pipe(
        map(() => null),
        catchError(() => of(null)),
        finalize(() => {
          // logoutRequestInFlight stays true — reset by resetLogoutLock() after a new login.
          this.clearSessionArtifacts();
          if (toastMessage) {
            this.toastService.show(toastMessage, 'error');
          }
          void this.router.navigateByUrl('/login', { replaceUrl: true });
        }),
      )
      .subscribe();
  }

  resetLogoutLock(): void {
    this.logoutRequestInFlight = false;
  }

  private clearSessionArtifacts(): void {
    this.crmAuthCookieService.clearAuthCookies();
    this.crmAuthFlowService.clearChallengeContext();
    this.crmAuthFlowService.clearPersistLogin();
    this.crmOnboardingAccessService.clear();
    this.crmPermissionStateService.clear();

    // Reset branding back to product defaults so the tenant's colors don't
    // bleed into the login screen after logout / 401 (also clears sessionStorage).
    this.crmBrandingStateService.resetToDefaultBranding();

    this.clearLastProtectedRoute();
    this.authService.logout();
  }

  isProtectedRoute(url: string): boolean {
    const path = `/${String(url || '').replace(/^\/+/, '')}`;

    if (path === '/' || path === '') {
      return false;
    }

    // Allowlist, not blocklist. A blocklist treats any unrecognised string as a
    // protected route, so a typo'd URL gets persisted as the "last protected
    // route" and is then restored on every subsequent load — bouncing the user
    // between the wildcard route and /login until site data is cleared.
    if (!isKnownAppUrl(path)) {
      return false;
    }

    const disallowedPrefixes = [
      '/login',
      '/verify',
      '/forgot-password',
      '/set-password',
      '/reset-password',
      '/error',
      '/manual-tenant',
      '/create-hq',
      '/application-under-review',
      '/in-review-thank-you',
    ];

    return !disallowedPrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
  }
}